/* eslint-disable camelcase */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SyntaxHighlighter from 'react-syntax-highlighter';

import BulmaContainer from 'react-bulma-components/lib/components/container';
import BulmaSection from 'react-bulma-components/lib/components/section';

import { css } from '@emotion/core';
import { Heading, TextLink } from '../atoms';

import {
  StyledSideContent,
  StyledTabContainer,
  StyledTab,
  StyledCodeblockWrapper,
  StyledCodeblock,
  styledContainer,
  styledCodeblockContainer,
  styledCopy,
  styledHeading,
  styledSubheading,
  customCodeBlockStyles,
} from './CodeBlockStyles';

import { createRandomId } from '../../util/createRandomId';

const renderHeading = (text, atts) => <Heading {...atts}>{Array.isArray(text) ? text : text.text}</Heading>;

const renderCodeBlock = (lang, content) => (
  <SyntaxHighlighter language={lang} style={customCodeBlockStyles}>
    {content}
  </SyntaxHighlighter>
);

const CodeBlock = ({ input }) => {
  const {
    text_color,
    background_color,
    background_image,
    heading,
    subheading,
    heading_color,
    subheading_color,
    headline,
    headline_color,
    copy,
    copy_color,
    code_link_url,
    code_link_text,
    link_url,
    link_text,
  } = input.primary;

  const codeblocks = input.fields;
  const [currentTab, setCurrentTab] = useState(0);

  return (
    <BulmaSection
      css={css`
        background-color: ${background_color};
        ${background_image && background_image.url ? `background-image: url(${background_image.url});` : null};
      `}
    >
      <BulmaContainer css={styledCodeblockContainer}>
        {heading && heading[0].text
          ? renderHeading(heading[0], {
              renderAs: 'h2',
              className: `${text_color === 'light' && !heading_color ? 'h2 white' : 'h2 darkblue'}`,
              style: { color: heading_color },
              css: styledHeading,
            })
          : null}
        {subheading && subheading[0].text
          ? renderHeading(subheading[0], {
              renderAs: 'p',
              className: `${text_color === 'light' && !subheading_color ? 'white medium' : 'darkgrey medium'}`,
              style: { color: subheading_color },
              subtitle: true,
              css: styledSubheading,
            })
          : null}
        <div css={styledContainer}>
          <StyledSideContent>
            {headline &&
              headline[0] &&
              headline[0].text &&
              renderHeading(headline[0], {
                style: headline_color && { color: headline_color },
                renderAs: 'h2',
                className: 'h2 darkblue',
              })}
            {renderHeading(copy, { style: copy_color && { color: copy_color }, css: styledCopy })}
            {link_text && link_text[0].text ? (
              <TextLink className={text_color === 'light' ? 'white medium arrow' : 'medium arrow'} url={link_url.url}>
                {link_text[0].text}
              </TextLink>
            ) : null}
          </StyledSideContent>
          {codeblocks.length <= 1 ? (
            <StyledCodeblockWrapper>
              <StyledCodeblock isSingleCodeblock="true">
                {renderCodeBlock(codeblocks[0].code_language, codeblocks[0].code_content[0].text)}
                {code_link_url && code_link_url.url && (
                  <TextLink url={code_link_url.url} className="arrow">
                    {code_link_text && code_link_text}
                  </TextLink>
                )}
              </StyledCodeblock>
            </StyledCodeblockWrapper>
          ) : (
            <StyledCodeblockWrapper>
              <StyledTabContainer>
                {codeblocks.map((codeblock, i) => (
                  <li key={createRandomId()}>
                    <StyledTab
                      active={currentTab === i}
                      key={codeblock.code_language}
                      onClick={() => setCurrentTab(i)}
                      onKeyPress={() => setCurrentTab(i)}
                      role="link"
                      tabIndex="0"
                    >
                      {`${codeblock.code_language[0].toUpperCase()}${codeblock.code_language.slice(1)}`}
                    </StyledTab>
                  </li>
                ))}
              </StyledTabContainer>
              {codeblocks.map((codeblock, i) => (
                <StyledCodeblock active={currentTab === i} key={createRandomId()}>
                  {renderCodeBlock(codeblock.code_language, codeblock.code_content[0].text)}
                </StyledCodeblock>
              ))}
            </StyledCodeblockWrapper>
          )}
        </div>
      </BulmaContainer>
    </BulmaSection>
  );
};

export default CodeBlock;

CodeBlock.propTypes = {
  input: PropTypes.object.isRequired,
};
